export const LANGUAGE_PAGES = {
  "english-to-arabic-translation": {
    type: "language",
    title: "English to Arabic Translation Services",
    meta: "Professional English to Arabic translation for business, legal, technical and certified documents. Upload your files and receive a clear quotation.",
    intro: "Arabic is spoken by more than 400 million people across some of the world's fastest-growing markets. Our English to Arabic translation service pairs your documents with professional native Arabic linguists who understand regional variation, right-to-left formatting and the formal registers business and official documents demand.",
    points: [
      "Business contracts, proposals and corporate communications",
      "Legal and certified documents for official use",
      "Technical manuals, datasheets and regulatory documentation",
      "Websites, ecommerce catalogues and marketing campaigns",
    ],
    faqs: [
      { q: "How much does English to Arabic translation cost?", a: "Pricing depends on word count, subject matter and urgency. Upload your document or enter a word count in our quote tool and you will receive an indicative estimate immediately, confirmed by our team." },
      { q: "Do you handle right-to-left formatting?", a: "Yes. Arabic documents are delivered with correct right-to-left layout, and our multilingual DTP service handles design files where required." },
      { q: "Can you certify the translation?", a: "Yes. Select Certified Translation in the quote form and our team will confirm certification requirements for your target authority." },
    ],
  },
  "english-to-french-translation": {
    type: "language",
    title: "English to French Translation Services",
    meta: "Professional English to French translation for business, legal, technical and marketing content. Fast quotations and quality-reviewed delivery.",
    intro: "French opens doors across Europe, Canada and francophone Africa. Our English to French translation service delivers accurate, natural French written by professional native linguists — from contracts and compliance documents to websites that read as if they were written in French from the start.",
    points: [
      "Legal contracts and certified official documents",
      "Ecommerce product catalogues and storefront localisation",
      "Marketing and brand content adapted for French audiences",
      "Technical and regulatory documentation",
    ],
    faqs: [
      { q: "European or Canadian French?", a: "Both. Tell us your target market in the quote form and we assign linguists accordingly." },
      { q: "How fast can you deliver?", a: "Standard delivery is typically 3–5 business days depending on volume; urgent 24–48 hour delivery is available for time-sensitive projects." },
      { q: "Is my document kept confidential?", a: "Yes. All documents are stored securely and handled confidentially throughout the translation process." },
    ],
  },
  "english-to-german-translation": {
    type: "language",
    title: "English to German Translation Services",
    meta: "Professional English to German translation for technical, legal, business and ecommerce content. Upload documents and receive a quotation.",
    intro: "Germany is Europe's largest economy and one of the most demanding markets for precision. Our English to German translation service is built for technical accuracy — engineering documentation, legal texts and business communications translated by professional native German linguists with subject-matter expertise.",
    points: [
      "Technical manuals, engineering and safety documentation",
      "Legal contracts and certified translations",
      "Business reports, tenders and corporate communications",
      "Website and software localisation for the DACH market",
    ],
    faqs: [
      { q: "Do you translate technical documentation?", a: "Yes. Technical and engineering content is assigned to specialist linguists and passes through structured quality review." },
      { q: "How is pricing calculated?", a: "By word count, language pair, subject matter and urgency. The quote tool gives an indicative estimate instantly." },
      { q: "Can you handle InDesign or formatted files?", a: "Yes — upload your files and our multilingual DTP service returns print-ready German layouts." },
    ],
  },
  "english-to-spanish-translation": {
    type: "language",
    title: "English to Spanish Translation Services",
    meta: "Professional English to Spanish translation for business, legal, medical and ecommerce content. Instant indicative quotes online.",
    intro: "Spanish connects you to more than 500 million speakers across Europe and the Americas. Our English to Spanish translation service covers both European and Latin American Spanish, delivered by professional native linguists and quality-reviewed before delivery.",
    points: [
      "Business and legal documents for Spanish-speaking markets",
      "Ecommerce and website localisation",
      "Marketing campaigns adapted for regional audiences",
      "Medical, technical and regulatory content",
    ],
    faqs: [
      { q: "European or Latin American Spanish?", a: "Both. Specify your audience when requesting your quote and we localise accordingly." },
      { q: "What file formats do you accept?", a: "PDF, DOCX, XLSX, PPTX, JPG and PNG among others — upload directly in the quote form." },
      { q: "How do I pay?", a: "Once you approve the quotation, you receive a secure payment link. Work begins when payment is received." },
    ],
  },
  "english-to-chinese-translation": {
    type: "language",
    title: "English to Chinese Translation Services",
    meta: "Professional English to Chinese (Simplified & Traditional) translation for business, technical and ecommerce content. Get a quote online.",
    intro: "Whether you are entering mainland China, Hong Kong or Taiwan, precision matters. Our English to Chinese translation service covers Simplified and Traditional Chinese, with professional native linguists who understand both the language and the business culture behind it.",
    points: [
      "Simplified and Traditional Chinese for every region",
      "Business contracts, tenders and corporate materials",
      "Ecommerce listings and marketplace localisation",
      "Technical and regulatory documentation",
    ],
    faqs: [
      { q: "Simplified or Traditional Chinese?", a: "Simplified is standard for mainland China and Singapore; Traditional for Hong Kong and Taiwan. Tell us your market in the quote form." },
      { q: "Can you localise our website?", a: "Yes — full website localisation including layout considerations for Chinese typography." },
      { q: "How long does it take?", a: "Standard projects typically complete in 3–5 business days; urgent delivery is available." },
    ],
  },
  "english-to-japanese-translation": {
    type: "language",
    title: "English to Japanese Translation Services",
    meta: "Professional English to Japanese translation for business, technical and marketing content. Upload documents for a clear quotation.",
    intro: "Japanese business culture rewards precision and formality. Our English to Japanese translation service pairs your content with professional native Japanese linguists who handle everything from technical documentation to brand voice with the register your audience expects.",
    points: [
      "Business proposals, contracts and corporate communications",
      "Technical manuals and product documentation",
      "Website, app and software localisation",
      "Marketing content adapted for Japanese audiences",
    ],
    faqs: [
      { q: "Do you handle keigo and formal registers?", a: "Yes. Linguists match the appropriate formality level for your audience and document type." },
      { q: "Can you translate our app?", a: "Yes — software and app localisation is a core service, including string files and app store content." },
      { q: "How do I get started?", a: "Upload your documents in the quote tool and receive an indicative estimate immediately." },
    ],
  },
};

export const INDUSTRY_PAGES = {
  "cosmetics-translation-services": {
    type: "industry",
    title: "Cosmetics & Beauty Translation Services",
    meta: "Specialist translation for cosmetics and beauty brands: product information, INCI labelling, claims and regulatory compliance documentation.",
    intro: "Cosmetics translation sits at the intersection of marketing and regulation. Product information files, INCI ingredient lists, claims substantiation and safety assessments all demand translators who understand both the beauty industry and the compliance frameworks behind it — from EU CPNP requirements to international labelling standards.",
    points: [
      "Product Information Files (PIFs) and safety documentation",
      "INCI lists, labelling and packaging translation",
      "Marketing, brand and ecommerce content for beauty markets",
      "Regulatory and compliance documentation for international launches",
    ],
    faqs: [
      { q: "Do you understand EU cosmetics regulation?", a: "Our specialist linguists regularly translate PIFs, CPSRs and labelling content aligned with EU Cosmetics Regulation requirements." },
      { q: "Can you translate packaging artwork?", a: "Yes — combined with our multilingual DTP service, packaging files are returned print-ready in every target language." },
      { q: "Which markets do you cover?", a: "We support 30+ languages across European, Middle Eastern, Asian and American markets." },
    ],
  },
  "legal-translation-services": {
    type: "industry",
    title: "Legal Translation Services",
    meta: "Professional legal translation for contracts, court documents, patents and official certificates. Confidential and quality reviewed.",
    intro: "Legal translation leaves no room for approximation. Contracts, court filings, patents and official certificates require linguists with legal expertise and an understanding of the target jurisdiction's terminology. Every legal project is handled confidentially and passes through structured quality review.",
    points: [
      "Contracts, agreements and terms of service",
      "Court documents, witness statements and arbitration materials",
      "Patents and intellectual property documentation",
      "Certified translations for official and government use",
    ],
    faqs: [
      { q: "Are your legal translations certified?", a: "Certified translation is available — select it in the quote form and our team will confirm the certification format your authority requires." },
      { q: "How is confidentiality handled?", a: "Documents are stored securely, access is restricted, and NDAs can be arranged on request." },
      { q: "Do you translate patents?", a: "Yes, patent and IP translation is handled by specialist legal-technical linguists." },
    ],
  },
  "technical-translation-services": {
    type: "industry",
    title: "Technical Translation Services",
    meta: "Specialist technical translation for manuals, engineering documentation, safety data sheets and regulatory files. Get a quote online.",
    intro: "Technical documentation must be unambiguous in every language. User manuals, engineering specifications, safety data sheets and regulatory files are translated by linguists with engineering and scientific backgrounds, using consistent terminology across your entire documentation set.",
    points: [
      "User manuals and installation guides",
      "Engineering specifications and CAD documentation",
      "Safety data sheets (SDS) and compliance files",
      "Regulatory submissions and technical files",
    ],
    faqs: [
      { q: "How do you keep terminology consistent?", a: "We maintain project glossaries and translation memories so repeated terms are translated identically across every document." },
      { q: "Which formats do you support?", a: "PDF, DOCX, XLSX, PPTX and image formats — formatted files can be returned print-ready via multilingual DTP." },
      { q: "Can you handle large documentation sets?", a: "Yes. Ongoing and high-volume technical programmes are scoped individually by our team." },
    ],
  },
  "certified-translation-services": {
    type: "industry",
    title: "Certified Translation Services",
    meta: "Certified translations for official use: birth certificates, legal documents, academic records and immigration papers. Fast turnaround.",
    intro: "When a translation must be accepted by an authority, certification matters. Our certified translation service covers personal, legal and academic documents for official submission — prepared by professional linguists and reviewed before delivery.",
    points: [
      "Birth, marriage and academic certificates",
      "Immigration and visa documentation",
      "Legal and court documents for official submission",
      "Company registration and notarial documents",
    ],
    faqs: [
      { q: "Will my authority accept the translation?", a: "Requirements vary by authority. Tell us where the document will be submitted and our team will confirm the appropriate certification." },
      { q: "How fast is certified translation?", a: "Short personal documents are typically completed within 1–2 business days; urgent options are available." },
      { q: "How do I send my documents?", a: "Upload scans or photos directly in the quote tool — clear JPG or PDF copies are sufficient in most cases." },
    ],
  },
};

export const ALL_LANDING_PAGES = { ...LANGUAGE_PAGES, ...INDUSTRY_PAGES };
